import { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import axios from "axios";
import Dashboard from "./pages/Dashboard";
import LandingPage from "./pages/LandingPage";
import { Toaster } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function FacebookPagePicker({ sessionId, onSelected }) {
  const [pages, setPages] = useState([]);
  const [sessionUserId, setSessionUserId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selecting, setSelecting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    axios.get(`${API}/auth/facebook/pages?session_id=${sessionId}`)
      .then(res => {
        setPages(res.data.pages || []);
        setSessionUserId(res.data.user_id);
      })
      .catch(() => setError("Failed to load Facebook pages."))
      .finally(() => setLoading(false));
  }, [sessionId]);

  const selectPage = async (pageId) => {
    setSelecting(true);
    try {
      await axios.post(`${API}/auth/facebook/select-page`, {
        session_id: sessionId,
        page_id: pageId,
      });
      onSelected(sessionUserId);
    } catch {
      setError("Failed to connect page. Please try again.");
      setSelecting(false);
    }
  };

  return (
    <div className="fb-page-picker-overlay">
      <div className="fb-page-picker-modal">
        <h2>Select a Facebook Page</h2>
        <p>Choose the page you want to connect to Content Radar.</p>
        {loading && <p className="fb-picker-loading">Loading pages...</p>}
        {error && <p className="fb-picker-error">{error}</p>}
        {!loading && !error && pages.length === 0 && (
          <p className="fb-picker-empty">No pages found for this account.</p>
        )}
        <ul className="fb-page-list">
          {pages.map(page => (
            <li key={page.id} className="fb-page-item">
              <button
                className="fb-page-btn"
                onClick={() => selectPage(page.id)}
                disabled={selecting}
              >
                <div className="fb-page-header">
                  <span className="fb-page-name">{page.name}</span>
                  {page.is_personal
                    ? <span className="fb-page-badge personal">Personal Profile</span>
                    : <span className="fb-page-badge page">Page</span>
                  }
                </div>
                {page.is_personal && (
                  <span className="fb-page-note">Read-only — Facebook API doesn't allow posting to personal profiles</span>
                )}
              </button>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function App() {
  const [userId, setUserId] = useState(null);
  const [fbPageSession, setFbPageSession] = useState(null);

  useEffect(() => {
    const storedUserId = localStorage.getItem('userId');
    if (storedUserId) {
      setUserId(storedUserId);
    }

    const params = new URLSearchParams(window.location.search);
    const connected = params.get('connected');
    const newUserId = params.get('user_id');
    const fbSelect = params.get('fb_page_select');

    if (fbSelect) {
      setFbPageSession(fbSelect);
      window.history.replaceState({}, document.title, "/");
      return;
    }

    if (connected && newUserId) {
      localStorage.setItem('userId', newUserId);
      setUserId(newUserId);
      window.history.replaceState({}, document.title, "/");
    }
  }, []);

  const handleFbPageSelected = (selectedUserId) => {
    setFbPageSession(null);
    if (selectedUserId) {
      localStorage.setItem('userId', selectedUserId);
      setUserId(selectedUserId);
    }
  };

  return (
    <div className="App">
      <Toaster position="top-right" richColors />
      {fbPageSession && (
        <FacebookPagePicker
          sessionId={fbPageSession}
          onSelected={handleFbPageSelected}
        />
      )}
      <BrowserRouter>
        <Routes>
          <Route path="/" element={
            userId ? <Dashboard userId={userId} setUserId={setUserId} /> : <LandingPage setUserId={setUserId} />
          } />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
